import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaCalculator, FaQuestionCircle, FaCloud } from 'react-icons/fa';

const ApiQuestionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get('questionId');
  const userQuestionsAssignmentId = searchParams.get('userQuestionsAssignmentId');
  const returnUrl = searchParams.get('returnUrl') || '/questions-answers';

  const [question, setQuestion] = useState(null);
  const [apiQuestions, setApiQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculatedResult, setCalculatedResult] = useState(null);

  useEffect(() => {
    // Prioritize state data if available (faster, no API call needed)
    if (location.state?.question) {
      const questionData = location.state.question;
      setQuestion(questionData);
      if (questionData.apiQuestions && questionData.apiQuestions.length > 0) {
        setApiQuestions(questionData.apiQuestions);
        const existingAnswers = {};
        questionData.apiQuestions.forEach(apiQ => {
          if (apiQ.userAnswer) {
            existingAnswers[apiQ.id] = apiQ.userAnswer;
          }
        });
        setAnswers(existingAnswers);
      } else if (questionData.id) {
        fetchApiQuestions(questionData.id);
      }
    } else if (questionId) {
      // Fallback: fetch question if not passed via state
      fetchQuestionAndApiQuestions();
    }
  }, [questionId, location.state]);

  const fetchQuestionAndApiQuestions = async () => {
    setLoading(true);
    try {
      // Fetch the question details first
      const questionResponse = await axios.get(`/questions-for-answers`);
      if (questionResponse.data.status) {
        const foundQuestion = questionResponse.data.questionsAssignedToUser?.find(
          q => q.id === parseInt(questionId)
        );
        if (foundQuestion) {
          setQuestion(foundQuestion);
          if (foundQuestion.apiQuestions && foundQuestion.apiQuestions.length > 0) {
            setApiQuestions(foundQuestion.apiQuestions);
            const existingAnswers = {};
            foundQuestion.apiQuestions.forEach(apiQ => {
              if (apiQ.userAnswer) {
                existingAnswers[apiQ.id] = apiQ.userAnswer;
              }
            });
            setAnswers(existingAnswers);
          } else {
            await fetchApiQuestions(foundQuestion.id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching question:', error);
      toastr.error(t('apiQuestions.failedToFetchQuestion'));
    } finally {
      setLoading(false);
    }
  };

  const fetchApiQuestions = async (qId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api-questions/questions/${qId}/api-questions`);
      if (response.data.status) {
        setApiQuestions(response.data.apiQuestions || []);
        
        // Initialize answers with existing user answers
        const existingAnswers = {};
        (response.data.apiQuestions || []).forEach(apiQ => {
          if (apiQ.userAnswer) {
            existingAnswers[apiQ.id] = apiQ.userAnswer;
          }
        });
        setAnswers(existingAnswers);
      } else {
        toastr.error(response.data.message || t('apiQuestions.failedToFetch'));
      }
    } catch (error) {
      console.error('Error fetching API questions:', error);
      toastr.error(t('apiQuestions.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  const detectInputType = (apiQuestionText, unit) => {
    const text = apiQuestionText.toLowerCase();
    if (unit === 'file_upload') return 'file';
    if (unit === 'date_range') return 'date';
    if (text.includes('date') || text.includes('period')) return 'date';
    if (text.includes('upload') || text.includes('bill')) return 'file';
    if (text.includes('percentage') || text.includes('%')) return 'number';
    if (text.includes('pesos') || text.includes('clp') || text.includes('spend')) return 'number';
    if (text.includes('select') || text.includes('choose')) return 'select';
    return 'text';
  };

  const getPlaceholder = (apiQuestionText, unit) => {
    if (unit) {
      if (unit.includes('/')) {
        return `${t('apiQuestions.enterValueIn')} ${unit}`;
      }
      return `${t('apiQuestions.enterUnit')} ${unit}`;
    }
    return t('apiQuestions.enterYourAnswer');
  };

  const handleAnswerChange = (apiQuestionId, value) => {
    setAnswers(prev => ({ ...prev, [apiQuestionId]: value }));
  };

  const handleSave = async () => {
    if (!question) {
      toastr.error(t('subquestions.questionDataNotAvailable'));
      return;
    }

    setLoading(true);
    try {
      const savePromises = Object.entries(answers)
        .filter(([_, answer]) => answer !== '' && answer !== null && answer !== undefined)
        .map(([apiQuestionId, answer]) =>
          axios.post('/api-questions/submit-api-question-answer', {
            apiQuestionId: parseInt(apiQuestionId),
            questionId: question.id,
            userQuestionsAssignmentId: question.userQuestionsAssignment || userQuestionsAssignmentId,
            answer: answer
          })
        );

      if (savePromises.length === 0) {
        toastr.warning(t('apiQuestions.answerAtLeastOne'));
        return;
      }

      await Promise.all(savePromises);
      toastr.success(t('apiQuestions.answersSavedSuccessfully'));
    } catch (error) {
      console.error('Error saving API question answers:', error);
      toastr.error(t('apiQuestions.failedToSaveAnswers'));
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!question) {
      toastr.error(t('subquestions.questionDataNotAvailable'));
      return;
    }

    // First save all answers
    await handleSave();
    
    setCalculating(true);
    try {
      const response = await axios.post('/api-questions/trigger-api-call', {
        questionId: question.id,
        userQuestionsAssignmentId: question.userQuestionsAssignment || userQuestionsAssignmentId
      });

      if (response.data.status) {
        setCalculatedResult(response.data.result);
        toastr.success(t('apiQuestions.calculationComplete', { answer: response.data.answerText }));
        
        // Navigate back after a short delay
        setTimeout(() => {
          navigate(returnUrl);
        }, 2000);
      } else {
        toastr.error(response.data.message || t('apiQuestions.failedToCalculate'));
      }
    } catch (error) {
      console.error('Error calculating via API:', error);
      toastr.error(error.response?.data?.message || t('apiQuestions.failedToCalculateViaApi'));
    } finally {
      setCalculating(false);
    }
  };

  const handleBack = () => {
    navigate(returnUrl);
  };

  const allQuestionsAnswered = apiQuestions.every(apiQ => 
    answers[apiQ.id] && answers[apiQ.id] !== '' && answers[apiQ.id] !== null
  );

  if (loading && !question) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('apiQuestions.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all duration-200"
              >
                <FaArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <FaCalculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-white text-2xl font-semibold">{t('apiQuestions.title')}</h1>
                  <p className="text-blue-100 text-sm">
                    {t('apiQuestions.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-8">
            {question && (
              <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">{t('answers.question')}:</h3>
                <p className="text-blue-700">{question.engagementQuestion}</p>
              </div>
            )}

            {loading && apiQuestions.length === 0 ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                <p className="text-gray-500">{t('apiQuestions.loading')}</p>
              </div>
            ) : apiQuestions.length > 0 ? (
              <>
                {calculatedResult && (
                  <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaCloud className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">{t('apiQuestions.calculationResult')}</h4>
                    </div>
                    <p className="text-green-700">
                      <strong>CO₂e:</strong> {calculatedResult.co2e_tons?.toFixed(2)} {calculatedResult.co2e_unit || 'tCO₂e'}
                    </p>
                    {calculatedResult.emission_factor_name && (
                      <p className="text-sm text-green-600 mt-1">
                        {t('apiQuestions.using')}: {calculatedResult.emission_factor_name}
                      </p>
                    )}
                  </div>
                )}

                {apiQuestions.map((apiQuestion, index) => {
                  const inputType = detectInputType(apiQuestion.apiQuestionText, apiQuestion.unit);
                  const placeholder = getPlaceholder(apiQuestion.apiQuestionText, apiQuestion.unit);
                  
                  return (
                    <div key={apiQuestion.id} className="mb-6">
                      <label className="block text-lg font-semibold text-gray-700 mb-3">
                        {index + 1}. {apiQuestion.apiQuestionText}
                        {apiQuestion.unit && apiQuestion.unit !== 'file_upload' && (
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({apiQuestion.unit})
                          </span>
                        )}
                      </label>
                      
                      {inputType === 'file' ? (
                        <div className="border-2 border-gray-200 border-dashed rounded-lg p-4 text-center hover:border-green-400 transition-colors">
                          <input
                            type="file"
                            className="hidden"
                            id={`file-${apiQuestion.id}`}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleAnswerChange(apiQuestion.id, file.name);
                              }
                            }}
                          />
                          <label
                            htmlFor={`file-${apiQuestion.id}`}
                            className="cursor-pointer text-green-600 hover:text-green-700"
                          >
{t('apiQuestions.clickToUploadFile')}
                          </label>
                          {answers[apiQuestion.id] && (
                            <p className="mt-2 text-sm text-gray-600">{answers[apiQuestion.id]}</p>
                          )}
                        </div>
                      ) : inputType === 'date' ? (
                        <input
                          type="date"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200"
                          value={answers[apiQuestion.id] || ''}
                          onChange={(e) => handleAnswerChange(apiQuestion.id, e.target.value)}
                        />
                      ) : (
                        <input
                          type={inputType === 'number' ? 'number' : 'text'}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200 text-gray-900"
                          placeholder={placeholder}
                          value={answers[apiQuestion.id] || ''}
                          onChange={(e) => handleAnswerChange(apiQuestion.id, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaQuestionCircle className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg">{t('apiQuestions.noApiQuestionsAvailable')}</p>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200 flex justify-between items-center">
            <button
              onClick={handleBack}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 flex items-center space-x-2"
            >
              <FaArrowLeft className="w-4 h-4" />
{t('common.back')}
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                disabled={loading || apiQuestions.length === 0}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSave className="w-4 h-4" />
                <span>{loading ? t('common.saving') : t('apiQuestions.saveAnswers')}</span>
              </button>
              
              {apiQuestions.length > 0 && (
                <button
                  onClick={handleCalculate}
                  disabled={calculating || !allQuestionsAnswered}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaCalculator className="w-4 h-4" />
                  <span>{calculating ? t('apiQuestions.calculating') : t('apiQuestions.calculateViaApi')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiQuestionsPage;

